/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-depth */
import {
    Latest,
    latestAppletVariableValueSchema,
    // latestOkVarValSchema,
} from '../../types/mes/applet-designs/appletDesign.latest.zod';
import { CausalError } from '@redviking/causal-error';

// In the context of a runtime every variable is a local variable. Deployment param values have been settled, and macro outputs are flattend
export type AppletRuntimeVariables = Record<string, Latest.AppletVariables.LocalAppletVariable>

export type ActiveOperator = {
    id: string;
    username: string;
    firstName: string | null;
    lastName: string | null;
    signedJwt: string;
};

/** ActiveOperator without the JWT — safe to send over the wire to relay clients */
export type ActiveOperatorInfo = Omit<ActiveOperator, 'signedJwt'>;

export const maybeOkAppletVarValueHelper = (val: unknown): Latest.AppletVariables.AppletVariableValue => {
    for (const validVarValueType of latestAppletVariableValueSchema.options) {
        try {
            const parsedVarVal = validVarValueType.parse(val);
            return parsedVarVal;
        } catch (e: any) {
            // invalid_type is expected since a variable can only be one type all others will fail. If the error is something else we would want to report that
            if ('issues' in e && Array.isArray(e.issues)) {
                for (const err of e.issues) {
                    if ('code' in err) {
                        if (err.code !== 'invalid_type') {
                            throw new CausalError('unknown zod error', err);
                        }
                    } else {
                        throw new CausalError('unknown error', err);
                    }
                }
            } else {
                // something other than a zod error occurred
                throw new CausalError('unknown error', e);
            }
            // else ignore because we are trying to parse the value with multiple schemas
        }
    }
    throw new CausalError(`Invalid var value: ${val}`);
};

export const camelToTitle = (camel: string) => camel.replace(/([A-Z])/gu, ' $1').replace(/^./u, (str) => str.toUpperCase());

/**
 * show desktop by default, if desktop is disabled the following will be checked in order: mobile, tablet, largeDisplay
 * if the object passed in is null it will return desktop
*/
export const getDefaultAspectRatio = (gridLayoutAspectRatios?: Latest.Screen.Designer.GridLayoutAspectRatios): Latest.Screen.Designer.GridLayoutAspectRatio => {
    if (!gridLayoutAspectRatios) {
        return 'desktop';
    }
    if (!gridLayoutAspectRatios.desktop.enabled) {
        if (gridLayoutAspectRatios.mobile.enabled) {
            return 'mobile';
        } else if (gridLayoutAspectRatios.tablet.enabled) {
            return 'tablet';
        } else {
            return 'largeDisplay';
        }
    }
    return 'desktop';
};

export const defaultValueHelper = (type: AppletRuntimeVariables[string]['type']) => {
     if (type === 'bool') {
        return {
            type,
            defaultValue: false,
        };
    } else if (type === 'num') {
       return {
           type,
           defaultValue: 0,
        };
    } else if (type === 'str') {
        return {
            type,
            defaultValue: '',
        };
    } else if (type === 'empty') {
        return {
            type,
            defaultValue: null,
        };
    } else {
        const unhandledType: never = type;
        throw new Error(`Unhandled variable type: ${unhandledType}`);
    }
};

// FUTURE: Right now it is confusing what would happen if a variable in a cfg was defined as a string but the input from the deployment param was a number
// When the day comes to resolve that, it should be handled here
export const defaultDeploymentValueHelper = (depParamVariableCfg: Latest.AppletVariables.DeploymentParamAppletVar, valueFromDeployment: Latest.AppletVariables.AppletVariableValue): AppletRuntimeVariables[string] => {
    if (typeof valueFromDeployment === 'boolean') {
        return {
            type: 'bool',
            kind: 'local',
            idx: depParamVariableCfg.idx,
            name: depParamVariableCfg.name,
            defaultValue: valueFromDeployment,
            sparkplug: depParamVariableCfg.sparkplug,
        };
    } else if (typeof valueFromDeployment === 'number') {
       return {
            type: 'num',
            kind: 'local',
            idx: depParamVariableCfg.idx,
            name: depParamVariableCfg.name,
            defaultValue: valueFromDeployment,
            sparkplug: depParamVariableCfg.sparkplug,
        };
    } else if (typeof valueFromDeployment === 'string') {
        return {
            type: 'str',
            kind: 'local',
            idx: depParamVariableCfg.idx,
            name: depParamVariableCfg.name,
            defaultValue: valueFromDeployment,
            sparkplug: depParamVariableCfg.sparkplug,
        };
    } else if (valueFromDeployment === null) {
        return {
            type: 'empty',
            kind: 'local',
            idx: depParamVariableCfg.idx,
            name: depParamVariableCfg.name,
            defaultValue: valueFromDeployment,
            sparkplug: depParamVariableCfg.sparkplug,
        };
    } else {
        const unhandledType: never = valueFromDeployment;
        throw new Error(`Unhandled variable value: ${unhandledType}`);
    }
};
export type GetCellNameParams<C extends Latest.Screen.Cells.AppletCell> = {
    cell: C;
    defaultText: string;
    designConfig: Latest.AppletDesignVersionConfig;
    distinguishableIdentifier: keyof C['attrs'] | null;
};

export function getCellName<C extends Latest.Screen.Cells.AppletCell> (payload: GetCellNameParams<C>): string {
    if (payload.cell.name) {
        return payload.cell.name;
    }
    if (payload.distinguishableIdentifier) {
        const distinguishableIdentifier = payload.cell.attrs[payload.distinguishableIdentifier as keyof typeof payload.cell.attrs];
        if (distinguishableIdentifier) {
            return distinguishableIdentifier;
        }
    }
    return payload.defaultText;
}

export type FindCellInCfgResp<T extends Latest.Screen.Cells.AppletCell> = {
    cellCfg: T,
    cellIdx: number,
    screenId: string,
    screenIdx: number,
    screen: Latest.Screen.Config,
}

export const findCellInCfg = <CELL extends Latest.Screen.Cells.AppletCell = Latest.Screen.Cells.AppletCell>(appletDesignVersionEntity: Latest.AppletDesignVersionConfig, predicate: (cell: Latest.Screen.Cells.AppletCell) => boolean): FindCellInCfgResp<CELL> | null => {
    let ret: FindCellInCfgResp<CELL> | null = null;
   const screens = appletDesignVersionEntity.screens;
    screens.forEach((screen, screenIdx) => {
        screen.cells.forEach((cell, cellIdx) => {
            if (predicate(cell)) {
                ret = {
                    screen,
                    cellIdx,
                    screenIdx,
                    screenId: screen.id,
                    cellCfg: cell as CELL,
                };
            }
        });
    });
    return ret;
};

export const getAllAppletVariables = (appletDesignVersionConfig: Latest.AppletDesignVersionConfig) => {
    const ret: Record<string, Latest.AppletVariables.AppletVariable | Latest.AppletVariables.GeneratedAppletVariable> = {
        ...Object.fromEntries(Object.entries(appletDesignVersionConfig.appletVariables).sort(([ , varAVal ], [ , varBVal ]) => {
            if (varAVal.idx > varBVal.idx) {
                return 1;
            } else if (varAVal.idx < varBVal.idx) {
                return -1;
            }
            return 0;
        })),
    };

    for (const m of appletDesignVersionConfig.macros) {
        if (m.type === 'materialLookup') {
            ret[m.attrs.materialLookupAttrs.outputVars.materialIdVar.varId] = m.attrs.materialLookupAttrs.outputVars.materialIdVar.varCfg;
            ret[m.attrs.materialLookupAttrs.outputVars.serialNumberVar.varId] = m.attrs.materialLookupAttrs.outputVars.serialNumberVar.varCfg;
            for (const udfVariable of m.attrs.materialLookupAttrs.outputVars.udfs) {
                ret[udfVariable.varCfg.varId] = udfVariable.varCfg.varCfg;
            }
        } else {
            const u: never = m.type;
            throw new Error(`Unknown macro type: ${u}`);
        }
    }

    return ret;
};
